import { FaCalendarDay, FaClipboardList, FaRunning, FaUser, FaUsers } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHaptics } from "@/hooks/useHaptics";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useSafeArea } from "./SafeAreaProvider";

// iOS Native Tab Bar - 5 tabs lineares sem elevação
const navItems = [
  { icon: FaCalendarDay, path: "/dashboard", label: "Hoje" },
  { icon: FaClipboardList, path: "/training", label: "Plano" },
  { icon: FaRunning, path: "/", label: "Run" },
  { icon: FaUsers, path: "/social", label: "Club" },
  { icon: FaUser, path: "/profile", label: "Perfil" },
];

export const BottomNav = () => {
  const location = useLocation();
  const { medium } = useHaptics();
  const { bottom } = useSafeArea();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkModalOpen = () => {
      setIsModalOpen(document.body.classList.contains('ybr-modal-open'));
    };
    
    // Observer para detectar mudanças na classe do body
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    checkModalOpen(); // Check inicial
    
    return () => observer.disconnect();
  }, []);
  
  // Buscar contagem de corredores próximos
  const { data: nearbyCount = 0 } = useQuery({
    queryKey: ['nearby-count'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const { data: location } = await supabase
          .from('user_locations')
          .select('latitude, longitude')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!location) return 0;

        const { data } = await supabase.functions.invoke('get-nearby-runners', {
          body: {
            user_id: user.id,
            location: { lat: location.latitude, lng: location.longitude },
            filters: { max_distance_km: 5 }
          }
        });

        return data?.count || 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 120000, // 2 min
    enabled: location.pathname !== '/' && location.pathname !== '/social' // Não buscar quando estiver correndo ou no social
  });
  
  const handleNavClick = () => {
    medium(); // Vibração média ao trocar de tab (iOS style)
  };
  
  // Não renderizar quando modal estiver aberto
  if (isModalOpen) {
    return null;
  }
  
  return (
    <nav 
      data-tabbar="true"
      data-fixed-header="true"
      data-position="bottom"
      role="navigation"
      aria-label="Navegação principal"
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-background backdrop-blur-xl border-t border-border/40 shadow-[0_-4px_12px_rgba(0,0,0,0.25)]"
      style={{ 
        position: 'fixed !important' as any,
        bottom: 0,
        left: 0,
        right: 0,
        height: `calc(49px + max(${bottom}px, env(safe-area-inset-bottom)))`,
        paddingBottom: `max(${bottom}px, env(safe-area-inset-bottom))`,
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        touchAction: 'none',
        zIndex: 9999,
        transform: 'translate3d(0, 0, 0)',
        willChange: 'transform',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden'
      }}
    >
      {/* iOS Native Tab Bar - 5 tabs lineares */}
      <div className="flex items-center justify-around h-full max-w-md mx-auto px-2">
        {navItems.map(({ icon: Icon, path, label }) => {
          const isActive = location.pathname === path;
          
          return (
            <Link
              key={path}
              to={path}
              onClick={handleNavClick}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
              role="tab"
              aria-selected={isActive}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full relative transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              {/* Icon - 28pt quando ativo, 25pt quando inativo (Perfil: 26pt/23pt) */}
              <div className={cn(
                "flex items-center justify-center transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                isActive 
                  ? (label === 'Perfil' ? "h-6 w-6" : "h-7 w-7")
                  : (label === 'Perfil' ? "h-[21px] w-[21px]" : "h-[25px] w-[25px]")
              )}>
                <Icon 
                  className={cn(
                    "transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                    isActive 
                      ? (label === 'Perfil' ? "w-6 h-6 text-primary" : "w-7 h-7 text-primary")
                      : (label === 'Perfil' ? "w-[21px] h-[21px] text-muted-foreground/70" : "w-6 h-6 text-muted-foreground/70"),
                    isActive && "drop-shadow-[0_0_8px_rgba(251,146,60,0.4)]"
                  )}
                  aria-hidden="true"
                />
                
                {/* Badge para Club */}
                {label === 'Club' && nearbyCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs bg-primary rounded-full"
                    aria-label={`${nearbyCount} ${nearbyCount === 1 ? 'corredor próximo' : 'corredores próximos'}`}
                  >
                    {nearbyCount > 9 ? '9+' : nearbyCount}
                  </Badge>
                )}
              </div>
              
              {/* Label - SF Pro Text 10pt */}
              <span className={cn(
                "text-xs font-medium transition-colors duration-200",
                isActive ? "text-primary" : "text-muted-foreground/70"
              )}>
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
